import PropTypes from "prop-types";
import { filter, find, groupBy, path, pathEq, uniq } from "ramda";
import React, { useEffect, useState } from "react";
import {
  Button,
  ButtonToolbar,
  FormLabel,
  Form,
  FormControl,
  ListGroup,
} from "react-bootstrap";
import { defineMessages, FormattedMessage } from "react-intl";

import DriveSuggest, { LABELS } from "../../../components/DriveSuggest.jsx";
import genericTranslations from "../../../utils/genericTranslations.js";

const messages = defineMessages({
  audiences: {
    id: "modules.UrlEntry.components.UrlEntryForm.audiences",
    description: "URL Entry audiences label.",
    defaultMessage: "Audiences",
  },
  name: {
    id: "modules.UrlEntry.components.UrlEntryForm.name",
    description: "URL entry name label.",
    defaultMessage: "Name",
  },
  noAudiences: {
    id: "modules.UrlEntry.components.UrlEntryForm.noAudiences",
    description: "URL entry no audiences label.",
    defaultMessage: "No groups or users paired",
  },
  url: {
    id: "modules.UrlEntry.components.UrlEntryForm.url",
    description: "URL entry URL label.",
    defaultMessage: "URL",
  },
  browsingType: {
    id: "modules.UrlEntry.components.UrlEntryForm.browsingType",
    description: "URL entry type of browsing.",
    defaultMessage: "Browsing Type",
  },
});

const browsingTypeOptions = [
  {
    key: 0,
    value: "Internal",
    label: "Internal",
  },
  {
    key: 1,
    value: "External",
    label: "External",
  },
];

const styles = {
  dropdownContainer: {
    backgroundColor: "#E9EAE9",
    padding: 20,
    marginBottom: 0,
  },
  listGroup: {
    marginBottom: 20,
  },
  listGroupItem: {
    display: "flex",
    justifyContent: "space-between",
  },
};

const UrlEntryForm = ({
  handleChange,
  handleSubmit,
  errors,
  isSubmitting,
  onClickDelete,
  setFieldValue,
  values,
}) => {
  const [audiences, setAudiences] = useState(values.audiences || []);

  useEffect(() => {
    setFieldValue("audiences", audiences);
  }, [audiences]);

  // Group the audiences
  const groupedAudiences = groupBy(
    (a) => LABELS[path(["value", "resourceType"], a)] || "Onbekend",
  )(audiences);

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Group controlId="name" className="mb-3">
        <FormLabel>
          <FormattedMessage {...messages.name} />
        </FormLabel>
        <FormControl
          type="text"
          autoComplete="off"
          value={values.name}
          onChange={handleChange}
          isInvalid={!values.name}
        />
        <Form.Control.Feedback type="invalid">
          {errors.name}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group controlId="url" className="mb-3">
        <FormLabel>
          <FormattedMessage {...messages.url} />
        </FormLabel>
        <FormControl
          type="text"
          autoComplete="off"
          value={values.url}
          onChange={handleChange}
          isInvalid={!values.url}
        />
        <Form.Control.Feedback type="invalid">
          {errors.url}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group controlId="browsingType" className="mb-3">
        <FormLabel>
          <FormattedMessage {...messages.browsingType} />
        </FormLabel>
        <Form.Control
          as="select"
          name="browsingType"
          isInvalid={!values.browsingType || values.browsingType === "-1"}
          aria-label="Select"
          value={values.browsingType}
          onChange={(e) => setFieldValue("browsingType", e.target.value)}
        >
          <option value={-1}>Select...</option>
          {browsingTypeOptions.map((browsingType) => (
            <option key={browsingType.value} value={browsingType.value}>
              {browsingType.label}
            </option>
          ))}
        </Form.Control>
        <Form.Control.Feedback type="invalid">
          {errors.browsingType}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group controlId="audiences">
        <FormLabel>
          <FormattedMessage {...messages.audiences} />
        </FormLabel>

        <div style={styles.dropdownContainer}>
          <DriveSuggest
            onSelect={(audience) => {
              if (
                !find(pathEq(["value", "id"], path(["value", "id"], audience)))(
                  audiences,
                )
              ) {
                const audienceArray = [...audiences, audience];

                setFieldValue("audiences", audienceArray);
                setAudiences([...audiences, audience]);
              }
            }}
          />

          <hr />

          {(!audiences || audiences.length === 0) && (
            <FormattedMessage {...messages.noAudiences} />
          )}

          {audiences && audiences.length > 0 && (
            <div>
              {uniq(Object.values(LABELS)).map((resourceType) => {
                if (!groupedAudiences[resourceType]) return null;

                return (
                  <div key={resourceType}>
                    <span>{resourceType}</span>
                    <ListGroup style={styles.listGroup}>
                      {groupedAudiences[resourceType].map((audience) => (
                        <ListGroup.Item
                          key={`audience-${audience.id}`}
                          style={styles.listGroupItem}
                        >
                          {audience.label}
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() =>
                              setAudiences(
                                filter(
                                  (a) =>
                                    path(["value", "id"], a) !==
                                    path(["value", "id"], audience),
                                )(audiences),
                              )
                            }
                          >
                            <FormattedMessage {...genericTranslations.delete} />
                          </Button>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {errors.audiences && <Form.Text muted>{errors.audiences}</Form.Text>}
      </Form.Group>

      <hr />

      <Form.Group controlId="formActions">
        <ButtonToolbar>
          {values.id && (
            <Button
              variant="danger"
              className="me-2"
              onClick={() => onClickDelete()}
            >
              <FormattedMessage {...genericTranslations.delete} />
            </Button>
          )}

          <Button
            type="submit"
            variant="light"
            disabled={isSubmitting || Object.keys(errors).length > 0}
          >
            <FormattedMessage {...genericTranslations.formSubmitLabel} />
          </Button>
        </ButtonToolbar>
      </Form.Group>
    </Form>
  );
};

UrlEntryForm.propTypes = {
  handleChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  onClickDelete: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,

  errors: PropTypes.shape({
    audiences: PropTypes.string,
    name: PropTypes.string,
    url: PropTypes.string,
    browsingType: PropTypes.string,
  }),

  values: PropTypes.shape({
    audiences: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.shape({
          id: PropTypes.string.isRequired,
          resourceType: PropTypes.string.isRequired,
        }),
      }),
    ),
    id: PropTypes.string,
    name: PropTypes.string,
    url: PropTypes.string,
    browsingType: PropTypes.string.isRequired,
  }).isRequired,
};

export default UrlEntryForm;
